import { useTranslation } from "@/localization/hooks/use-translation";
import {
  formatByteSize,
  formatCatalogDate,
  sumTrackDuration,
} from "@/music-catalog/utils/catalog-view";
import { useDisplaySettingsStore } from "@/settings/store/use-display-settings-store";
import { formatAlbumLength } from "@/settings/utils/format-album-length";

import type { LibraryProject } from "@/cloud/types/types";

/**
 * Everything the cloud knows about a project, set as liner notes.
 *
 * A description list rather than rows of divs, because that is what this is:
 * each line is a term and its value, and the markup saying so is what lets a
 * screen reader move between them as pairs.
 *
 * Not a card. Boxing these put a bordered panel beside the tracklist and gave
 * the page two competing objects, when what this actually is, is the small print
 * — the pressing details on the back of a sleeve. It is set the way small print
 * is set: the terms quiet and lettered wide, the values in the same tabular
 * figures the tracklist uses, each pair on its own hairline. That reads as a
 * record's details rather than as a second panel of data.
 *
 * Facts only, and only the ones that arrived over the wire. The artwork's own
 * dimensions are not among them — they live inside the project's bytes, and
 * reading them would mean parsing a document here.
 */
export const ProjectFacts = ({ project }: { project: LibraryProject }) => {
  const { language, t } = useTranslation();
  const albumLengthFormat = useDisplaySettingsStore((state) => state.albumLengthFormat);
  const length = sumTrackDuration(project.tracks);
  const lengthText = formatAlbumLength({
    format: albumLengthFormat,
    language,
    seconds: length.seconds,
  });
  const synced = formatCatalogDate(project.updatedAt, language);
  const changed = formatCatalogDate(project.deviceUpdatedAt, language);
  const trackCount = project.trackCount ?? project.tracks.length;

  const facts: { label: string; value: string }[] = [
    { label: t("catalog.tracks"), value: String(trackCount) },
    ...(length.seconds > 0
      ? [
          {
            label: t("catalog.length"),
            // Said to be partial when it is, because a running time quietly
            // short by three tracks is worse than one labelled as incomplete.
            value: length.isComplete
              ? lengthText
              : t("catalog.lengthPartial", { duration: lengthText }),
          },
        ]
      : []),
    { label: t("project.size"), value: formatByteSize(project.byteLength, language) },
    ...(synced === undefined ? [] : [{ label: t("project.updated"), value: synced }]),
    ...(changed === undefined ? [] : [{ label: t("project.deviceUpdated"), value: changed }]),
    { label: t("project.revision"), value: String(project.revision) },
    { label: t("project.fileVersion"), value: String(project.projectFileVersion) },
  ];

  return (
    <dl className="flex flex-col">
      {facts.map((fact) => (
        <div
          key={fact.label}
          className="flex items-baseline justify-between gap-4 border-t border-border/50 py-2.5 first:border-t-0 first:pt-0"
        >
          <dt className="shrink-0 text-[0.6875rem] font-medium tracking-[0.08em] text-muted-foreground uppercase">
            {fact.label}
          </dt>
          {/*
            Wrapped rather than truncated. These values are short by nature
            except for one — a running time the tracklist could not complete says
            so in words — and truncating turned the only fact here that carries a
            caveat into "7 min (some tracks have n…", which reads as a complete
            running time with noise after it.
          */}
          <dd className="min-w-0 text-right text-sm tabular-nums text-pretty">{fact.value}</dd>
        </div>
      ))}

      {project.forkedFromDesktopProjectId !== null && (
        <div className="mt-3 border-t border-border/50 pt-3 text-xs text-muted-foreground">
          {t("project.forkedFrom")}
        </div>
      )}
    </dl>
  );
};
